<template>
  <div class="row">
    <div class="col-md-9">
      <p>{{ item.name }}</p>
      <p class="metadata">
        <span v-if="item.supplier" v-html="`<b>Fornecedor:</b> ${item.supplier}<br>`"></span>
        <span v-if="item.document && item.document.length > 0" v-html="`<b>CNPJ/CPF:</b> ${item.document}<br>`"></span>
        <span v-if="item.city && item.city.length > 0" v-html="`<b>Localidade:</b> ${item.city}<br>`"></span>
        <span v-if="item.buyer && item.buyer.length > 0" v-html="`<b>Órgão:</b> ${item.buyer}<br>`"></span>
        <span v-if="item.description && item.description.length > 0" v-html="`<b>Descrição:</b> ${item.description}<br>`"></span>
        <span v-if="item.source === 'DIRECT_QUOTATION'" v-html="`<b>Cotação realizada em</b>: ${item.formatted_date}<br>`"></span>
        <span v-else v-html="`<b>Busca realizada em</b>: ${item.formatted_date}<br>`"></span>
        <span v-if="item.source_name && item.source_name.length > 0" v-html="`<b>Fonte:</b> ${item.source_name}<br>`"></span>
        <span v-if="item.link && item.link.length > 0">
          <b>Link:</b> 
          <a :href="getLinkRedirection(item.source, item.link)" target="_blank">{{ item.link }}</a>
          <br>
        </span>
        <strong v-html="`<b>Suporte Legal: </b>${item.legal_support || 'N/A'}`"></strong>
      </p>
    </div>
    <div class="col-md-3 d-flex flex-column align-items-end justify-content-center">
      <p class="mb-1 mb-auto">
        <img v-if="getSourceLogo(item.source)" :src="getSourceLogo(item.source)" height="25" :alt="item.source">
      </p>
      
      <p v-if="shouldShowOriginalPrice(item)" class="mb-0 metadata">
        Original: {{ formatCurrency(item.price) }} / {{ item.unit }}
      </p>
      
      <p class="mb-1 item-price">{{ formatCurrency(getResolvedPrice(item)) }}</p>
      <p class="mb-1 badge text-bg-light">{{ item.custom_unit || item.unit }}</p>
      <p class="mb-1 mb-auto metadata">
        <button 
          type="button" 
          class="btn btn-sm btn-link" 
          @click="togglePopover($event, 'Variação entre o preço deste item em relação à média dos outros itens. Valores maiores que 25% em relação à média são considerados excessivamente altos. Valores inferiores a 75% são considerados inexequíveis.')"
        >
          <i class="fas fa-question-circle"></i>
        </button>
        <strong class="align-middle">Variação: {{ item.variation }}%</strong>
        <br>
        <span v-if="Math.abs(item.variation) > 25" class="badge text-bg-danger">Alta variação</span>
        <span v-else class="badge text-bg-primary">Variação normal</span>
      </p>
    </div>
    
    <div class="col-md-12">
      <p v-if="hasInflationAdjustment(item) && item.adjustment_details" class="metadata">
        <i class="bi bi-info-circle me-1"></i>
        {{ getPriceDateText(item) }} atualizado por 
        <strong>{{ item.adjustment_details.index_type }}</strong>
        ({{ item.adjustment_details.percentage }}%) até {{ formatMonthYear(item.adjustment_details.current_date) }}
      </p>
      
      <p class="d-print-none">
        <button 
          @click="removeItem(item.id)" 
          class="btn btn-danger btn-sm me-3"
          :disabled="loading"
        >
          Remover
        </button>
        <button 
          @click="adjustUnit(item.id)" 
          class="btn btn-outline-primary btn-sm me-3"
          :disabled="loading"
        >
          Ajustar unidade
        </button>
        <button 
          v-if="hasInflationAdjustment(item)"
          @click="viewPriceAdjustment(item.id)" 
          class="btn btn-primary btn-sm me-3"
          :disabled="loading"
        >
          Ver Reajuste de Preço
        </button>
        <button 
          v-else
          @click="createPriceAdjustment(item.id)" 
          class="btn btn-outline-primary btn-sm me-3"
          :disabled="loading"
        >
          Reajuste de Preço
        </button>
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReportItemDetails',
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      activePopover: null
    }
  },
  methods: {
    formatCurrency(value) {
      if (!value) return 'R$ 0,00'
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value)
    },
    getSourceLogo(source) {
      const logos = {
        'PNCP': '/assets/pncp.png',
        'NF': '/assets/portal.png',
        'DER': '/assets/der.png',
        'CGOV': '/assets/cgov.svg',
        'TCE': '/assets/tcemg.png',
        'LICITAR_DIGITAL': '/assets/licitar_digital.png',
        'BPS': '/assets/bps.png',
        'LICITACOES_E': '/assets/licitacoes_e.png',
        'TRANSPARENCIA_MG': '/assets/transparencia_mg.png',
        'ComprasBR': '/assets/comprasbr.svg',
        'BLL': '/assets/bll.png',
        'BNC': '/assets/bnc.png',
        'CONAB': '/assets/conab.png',
        'LicitaMais': '/assets/licitamais.png',
        'SICRO': '/assets/dnit.png',
        'SINAPI': '/assets/sinapi.png',
        'AMERICANAS': '/assets/americanas.png'
      }
      return logos[source] || null
    },
    getLinkRedirection(source, url) {
      return `/link_redirection?source=${encodeURIComponent(source)}&url=${encodeURIComponent(url)}`
    },
    hasInflationAdjustment(item) {
      return item.has_inflation_adjustment
    },
    getResolvedPrice(item) {
      // Se tem ajuste de inflação, usar o preço ajustado pela inflação
      if (item.has_inflation_adjustment && item.inflation_price) {
        return item.inflation_price
      }
      // Caso contrário, usar o preço customizado ou o preço original
      return item.custom_price || item.price
    },
    getPriceDateText(item) {
      if (item.formatted_price_date) {
        return `Preço de ${new Date(item.formatted_price_date).toLocaleDateString('pt-BR')}`
      }
      return 'Preço original'
    },
    formatMonthYear(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    },
    togglePopover(event, content) {
      // Close any existing popover first
      if (this.activePopover) {
        this.activePopover.dispose()
        this.activePopover = null
      }
      
      // Create new popover
      const popover = new bootstrap.Popover(event.target, {
        content: content,
        trigger: 'manual',
        placement: 'top'
      })
      
      popover.show()
      this.activePopover = popover
      
      // Close popover when clicking outside
      setTimeout(() => {
        document.addEventListener('click', this.closePopover, true)
      }, 100)
    },
    closePopover(event) {
      if (this.activePopover && !event.target.closest('.popover') && !event.target.closest('.btn')) {
        this.activePopover.dispose()
        this.activePopover = null
        document.removeEventListener('click', this.closePopover, true)
      }
    },
    async removeItem(itemId) {
      if (!confirm('Tem certeza que deseja remover este item?')) return
      
      this.loading = true
      try {
        const response = await fetch(`/report_group_items/${itemId}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            'Accept': 'application/json'
          }
        })
        
        if (response.ok) {
          this.$emit('item-removed', itemId)
        } else if (response.status === 404) {
          // Item not found - already deleted, treat as success (idempotent)
          this.$emit('item-removed', itemId)
        } else {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
          throw new Error(errorData.error || 'Failed to remove item')
        }
      } catch (error) {
        console.error('Error removing item:', error)
        alert(`Erro ao remover item: ${error.message}`)
      } finally {
        this.loading = false
      }
    },
    adjustUnit(itemId) {
      window.location.href = `/report_group_items/${itemId}/edit`
    },
    viewPriceAdjustment(itemId) {
      // Navigate to the price inflation adjustment show page using the adjustment ID
      const adjustmentId = this.item.price_inflation_adjustment_id
      if (adjustmentId) {
        window.location.href = `/price_inflation_adjustments/${adjustmentId}`
      }
    },
    createPriceAdjustment(itemId) {
      window.location.href = `/price_inflation_adjustments/new?report_group_item_id=${itemId}`
    },
    shouldShowOriginalPrice(item) {
      // Mostrar preço original se há ajuste de inflação OU se há preço customizado diferente do original
      return item.has_inflation_adjustment || (item.custom_price && item.custom_price !== item.price)
    }
  },
  beforeUnmount() {
    if (this.activePopover) {
      this.activePopover.dispose()
    }
    document.removeEventListener('click', this.closePopover, true)
  }
}
</script>

